import { PressResponder, usePress } from '@react-aria/interactions';
import { mergeRefs, useUpdateEffect } from '@react-aria/utils';
import clsx from 'clsx';
import { ComponentProps, ReactElement, useCallback, useMemo, useRef } from 'react';
import { Key } from 'react-aria';
import {
  Cell,
  Column,
  Row,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
  SortDescriptor,
  ColumnRenderProps,
  ColumnProps,
  TableHeaderProps as AriaTableHeaderProps,
  TableBodyProps as AriaTableBodyProps,
  RowProps,
  CellProps,
  Collection,
  useTableOptions,
} from 'react-aria-components';
import Checkbox from '~/components/inputs/Checkbox/Checkbox';
import { HStack } from '~/components/layout/Stack';
import IconArrowDown from '~/icons/icon-arrow-down.svg?react';
import IconArrowUp from '~/icons/icon-arrow-up.svg?react';
import { useActivate, useIsActive, useIsChecked, useSelect, useSelectAll } from '~/state/selection';
import { useSetSortingWithDirection, useSorting } from '~/state/tableSorting';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import styles from './Table.module.scss';
import Icon from '../Icon';

type TableChild =
  ReactElement<TableHeaderProps<object>, typeof TableHeader> | ReactElement<TableBodyProps<object>, typeof TableBody>;
type TableChildren = TableChild | TableChild[] | false | null;

interface TableProps extends Omit<ComponentProps<typeof AriaTable>, 'children'> {
  children?: TableChildren;
  fullWidth?: boolean;
  isSortable?: boolean;
  scrollResetKey?: unknown;
}

function PressResponderRegistration() {
  // Registers a pressable descendant for PressResponder so we don't get the
  // "without a pressable child" warning when the table has no interactive rows
  // (e.g. empty/loading state).
  usePress({ isDisabled: true });
  return null;
}

export const Table = (props: TableProps) => {
  if ((props.selectionMode ?? 'none') === 'none') {
    return <BaseTable {...props} />;
  }

  return <SelectableTable {...props} />;
};

const BaseTable = (props: TableProps) => {
  return props.isSortable === false ? <UnsortableBaseTable {...props} /> : <SortableBaseTable {...props} />;
};

const UnsortableBaseTable = (props: TableProps) => {
  return <BaseTableContent {...props} />;
};

const SortableBaseTable = ({ isSortable: _isSortable, ...props }: TableProps) => {
  const sorting = useSorting();
  const setSorting = useSetSortingWithDirection();

  const onSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      if (typeof descriptor.column === 'number') throw new Error('Column index sorting is not supported');
      setSorting({ sort: descriptor.column, direction: descriptor.direction === 'ascending' ? 'asc' : 'desc' });
    },
    [setSorting],
  );

  const sortDescriptor: SortDescriptor | undefined = useMemo(() => {
    if (!sorting?.sort) return undefined;
    return {
      column: sorting.sort,
      direction: sorting.direction === 'asc' ? 'ascending' : 'descending',
    };
  }, [sorting]);

  return <BaseTableContent onSortChange={onSortChange} sortDescriptor={sortDescriptor} {...props} />;
};

const BaseTableContent = ({
  className,
  fullWidth,
  isSortable: _isSortable,
  ref,
  scrollResetKey,
  selectionMode = 'none',
  selectionBehavior = 'toggle',
  ...props
}: TableProps) => {
  const tableRef = useRef<HTMLDivElement | HTMLTableElement>(null);
  const mergedRef = useMemo(() => mergeRefs(tableRef, ref), [ref]);

  useUpdateEffect(() => {
    if (scrollResetKey === undefined) return;

    tableRef.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);

  // Row actions are powered by internal usePress handlers.
  // PressResponder injects allowTextSelectionOnPress into that context,
  // so text selection stays enabled.
  return (
    <PressResponder allowTextSelectionOnPress>
      <PressResponderRegistration />
      <AriaTable
        className={clsx(styles.table, { [styles.fullWidth!]: fullWidth }, className)}
        ref={mergedRef}
        selectionMode={selectionMode}
        selectionBehavior={selectionBehavior}
        {...props}
      />
    </PressResponder>
  );
};

const SelectableTable = (props: TableProps) => {
  const setActive = useActivate();
  const select = useSelect();

  const handleRowPress = (key: Key) => {
    const selectionMode = props.selectionMode ?? 'none';
    const selectionBehavior = props.selectionBehavior ?? 'toggle';
    const id = key.toString();

    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;

    assertIsDefined(id, 'Row id is required to handle row press');

    if (selectionBehavior === 'replace') {
      select(id.toString(), selectionMode === 'single');
    } else {
      setActive(id.toString());
    }
  };

  return <BaseTable onRowAction={(key) => handleRowPress(key)} {...props} />;
};

type TableHeaderProps<T extends object> = AriaTableHeaderProps<T> & {
  sticky?: boolean;
  /**
   * Total number of rows in the table.
   *
   * **Required** when `selectionBehavior="toggle"` and `selectionMode="multiple"`.
   * Ignored when `selectionBehavior` is not "toggle".
   */
  rowCount?: number;
  /**
   * Number of currently selected rows.
   *
   * **Required** when `selectionBehavior="toggle"` and `selectionMode="multiple"`.
   * Ignored when `selectionBehavior` is not "toggle".
   */
  numSelected?: number;
};

export const TableHeader = <T extends object>({
  className,
  sticky,
  children,
  columns,
  rowCount,
  numSelected,
  ...props
}: TableHeaderProps<T>) => {
  const { selectionBehavior, selectionMode } = useTableOptions();

  if (selectionBehavior !== 'toggle' && (rowCount !== undefined || numSelected !== undefined)) {
    // eslint-disable-next-line no-console
    console.warn(
      'rowCount and numSelected props are only used when selectionBehavior is "toggle". They will be ignored.',
    );
  }

  return (
    <AriaTableHeader className={clsx(styles.header, { [styles.sticky!]: sticky }, className)} {...props}>
      {selectionBehavior === 'toggle' && (
        <TableSelectionColumn selectionMode={selectionMode} rowCount={rowCount} numSelected={numSelected} />
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaTableHeader>
  );
};

interface TableSelectionColumnProps {
  selectionMode: 'none' | 'single' | 'multiple';
  rowCount?: number;
  numSelected?: number;
}

const TableSelectionColumn = ({ selectionMode, rowCount, numSelected }: TableSelectionColumnProps) => {
  if (selectionMode === 'single') {
    return <TableColumn className={styles.checkboxColumn} id="checkbox" />;
  }

  if (selectionMode !== 'multiple') {
    return null;
  }

  assertIsDefined(rowCount, 'rowCount is required when selectionMode is "multiple"');
  assertIsDefined(numSelected, 'numSelected is required when selectionMode is "multiple"');

  return <TableCheckboxColumn id="checkbox" rowCount={rowCount} numSelected={numSelected} />;
};

interface TableColumnRenderProps extends ColumnRenderProps {
  align: 'left' | 'center' | 'right';
}

interface TableColumnProps extends Omit<ColumnProps, 'children' | 'minWidth'> {
  /**
   * The minimum width of the column.
   *
   * **⚠️ Warning:** When using Virtualizer with TableLoadMoreItem, prefer using fixed `width` instead of `minWidth`.
   * The TableLayout virtualizes both horizontally and vertically, calculating width and height for each cell.
   * Using `minWidth` can cause the TableLoadMoreItem row to not update its width correctly on resize,
   * leading to horizontal scroll issues.
   * Replace TableLoadMoreItem with an alternative infinite loading solution if you need to use `minWidth`.
   */
  minWidth?: ColumnProps['minWidth'];
  align?: 'left' | 'center' | 'right';
  children?: React.ReactNode | ((values: TableColumnRenderProps) => React.ReactNode);
}

export const TableColumn = ({ className, children, minWidth = 0, align = 'left', ...props }: TableColumnProps) => {
  return (
    <Column className={clsx(styles.column, className)} minWidth={minWidth} {...props}>
      {(columnProps) =>
        typeof children === 'function' ? (
          children({ ...columnProps, align })
        ) : (
          <TableColumnContent {...columnProps} align={align}>
            {children}
          </TableColumnContent>
        )
      }
    </Column>
  );
};

interface TableColumnContentProps extends ColumnRenderProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

const TableColumnContent = ({ allowsSorting, sortDirection, children, align = 'left' }: TableColumnContentProps) => {
  return (
    <HStack gap={1} align="center" className={clsx(styles.columnContent, styles[align])}>
      {children}
      {allowsSorting && sortDirection === 'ascending' && (
        <Icon component={IconArrowDown} size="sm" className={styles.sortingIcon} />
      )}
      {allowsSorting && sortDirection === 'descending' && (
        <Icon component={IconArrowUp} size="sm" className={styles.sortingIcon} />
      )}
    </HStack>
  );
};

interface TableCheckboxColumnProps extends Omit<TableColumnProps, 'allowsSorting' | 'width' | 'children'> {
  /**
   * The width of the column. This prop only applies when the `<Table>` is wrapped in a `<ResizableTableContainer>`.
   * If not using a virtualizer, the width can be managed with the CSS variable `--selection-column-width`.
   */
  width?: TableColumnProps['width'];
  rowCount: number;
  numSelected: number;
}

const TableCheckboxColumn = ({ rowCount, numSelected, width = 48, ...props }: TableCheckboxColumnProps) => {
  const selectAll = useSelectAll();
  return (
    <TableColumn className={styles.checkboxColumn} width={width} {...props}>
      <Checkbox
        aria-label="Select all"
        slot="selection"
        checked={rowCount > 0 && numSelected === rowCount}
        isIndeterminate={numSelected > 0 && numSelected < rowCount}
        isDisabled={rowCount === 0}
        onChange={selectAll}
      />
    </TableColumn>
  );
};

type TableBodyProps<T extends object> = AriaTableBodyProps<T>;

export const TableBody = <T extends object>({ className, ...props }: TableBodyProps<T>) => {
  return <AriaTableBody className={className} {...props} />;
};

type RenderCheckboxCell<T extends object> = (node: React.ReactNode, rowProps: TableRowProps<T>) => React.ReactNode;

// react-aria caches a Row's dynamic cells and only re-renders them when the
// Row's `value`/`dependencies` change. Forward them to `<Row>` so cells refresh when the
// row data changes. `value` is typed independently of the column type `T` (react-aria
// ties them together, but here they differ) and only used for cache comparison.
type TableRowProps<T extends object> = Omit<RowProps<T>, 'value'> & {
  renderCheckboxCell?: RenderCheckboxCell<T>;
  value?: unknown;
};

const asRowValue = <T extends object>(value: unknown) => value as RowProps<T>['value'];

export const TableRow = <T extends object>(props: TableRowProps<T>) => {
  const { selectionMode } = useTableOptions();

  if (selectionMode === 'none') {
    return <BaseTableRow {...props} />;
  }

  return <SelectableTableRow {...props} />;
};

const BaseTableRow = <T extends object>(props: TableRowProps<T>) => {
  const {
    id,
    columns,
    value,
    dependencies,
    className,
    children,
    renderCheckboxCell: _renderCheckboxCell,
    ...rowProps
  } = props;

  return (
    <Row
      {...rowProps}
      value={asRowValue<T>(value)}
      dependencies={dependencies}
      className={clsx(styles.row, className)}
      id={id}
    >
      <Collection items={columns}>{children}</Collection>
    </Row>
  );
};

const SelectableTableRow = <T extends object>(props: TableRowProps<T>) => {
  const { id, columns, value, dependencies, className, children, renderCheckboxCell, ...rowProps } = props;
  const { selectionBehavior } = useTableOptions();
  const isActive = useIsActive();
  const isChecked = useIsChecked();

  return (
    <Row
      className={clsx(
        styles.row,
        {
          [styles.checked!]: isDefined(id) && isChecked(id.toString()),
          [styles.active!]: isDefined(id) && isActive(id.toString()),
        },
        className,
      )}
      id={id}
      value={asRowValue<T>(value)}
      dependencies={dependencies}
      {...rowProps}
    >
      {selectionBehavior === 'toggle' && (
        <CheckboxCell id={id} rowProps={props} renderCheckboxCell={renderCheckboxCell} />
      )}
      <Collection items={columns}>{children}</Collection>
    </Row>
  );
};

type CheckboxCellProps<T extends object> = CellProps & {
  rowProps: TableRowProps<T>;
  renderCheckboxCell?: RenderCheckboxCell<T>;
};

const CheckboxCell = <T extends object>({ id, rowProps, renderCheckboxCell, ...props }: CheckboxCellProps<T>) => {
  const isChecked = useIsChecked();
  const select = useSelect();
  const { selectionMode } = useTableOptions();

  assertIsDefined(id, 'id is required when selectionBehavior is "toggle"');

  const onChange = () => {
    select(id.toString(), selectionMode === 'single');
  };

  const checkbox = <Checkbox onChange={onChange} checked={isChecked(id.toString())} />;

  return (
    <TableCell align="center" className={styles.checkboxCell} {...props}>
      {renderCheckboxCell ? renderCheckboxCell(checkbox, rowProps) : checkbox}
    </TableCell>
  );
};

interface TableCellProps extends CellProps {
  align?: 'left' | 'center' | 'right';
}

export const TableCell = ({ className, align = 'left', children, ...props }: TableCellProps) => {
  return (
    <Cell className={clsx(styles.cell, styles[align], className)} {...props}>
      {(cellProps) => {
        const content = typeof children === 'function' ? children(cellProps) : children;
        const justify = align === 'left' ? 'start' : align === 'right' ? 'end' : 'center';
        return (
          <HStack align="center" justify={justify} className={styles.cellContent}>
            {content}
          </HStack>
        );
      }}
    </Cell>
  );
};
